import React from "react";
import { IonText, useIonAlert } from "@ionic/react";
import { useMarketMode } from "../../providers/marketMode";
import cln from "classnames";
import { toast } from "react-toastify";
import { MarketDeliveryType } from "@core/types";
import { useCustomerAddressSelectData } from "../../providers/CustomerAddresses/CustomerAddressesStateContext";
import { useHistory } from "react-router-dom";
import { useActiveOrMainShippingZone } from "../../providers/shippingZones";
import useGetCurrentAddress from "../../hooks/useGetCurrentAddress";
import { useTranslation } from "react-i18next";

const modes = ["express", "market"] as const;

interface ExpressMarketSwitcherProps {
  wrapperClassname?: string;
}

const ExpressMarketSwitcher = ({ wrapperClassname }: ExpressMarketSwitcherProps) => {
  const { t } = useTranslation();
  const history = useHistory();
  const expressAvailableId = React.useRef<any>(null);
  const expressNotAvailableId = React.useRef<any>(null);
  const createExpressAddressId = React.useRef<any>(null);
  const toastTodayId = React.useRef<any>(null);
  const [presentAlert] = useIonAlert();

  const { activeShippingZone } = useActiveOrMainShippingZone();
  const { id: activeShippingZoneId } = activeShippingZone;

  const address = useGetCurrentAddress();
  const { setIsAddressSelectOpen } = useCustomerAddressSelectData();
  const { mode, toggleMode, setExpressPointId, resetExpressPointId } = useMarketMode();

  const handleModeChange = (value: MarketDeliveryType) => {
    presentAlert({
      header: t("Warning"),
      message: t("When switching the mode, the cart will be cleaned, continue?"),
      backdropDismiss: false,
      keyboardClose: false,
      buttons: [
        {
          text: t("Ok"),
          handler: () => {
            toggleMode(value, activeShippingZoneId);

            if (value === "express") {
              if (address && !!address.expressPointId) {
                setExpressPointId(address.expressPointId);
                if (!toast.isActive(expressAvailableId.current)) {
                  expressAvailableId.current = toast.success(t("We'll deliver in hour"));
                }
              } else {
                setIsAddressSelectOpen(true);
                toggleMode("market", activeShippingZoneId);
                resetExpressPointId();
                if (!address) {
                  if (!toast.isActive(createExpressAddressId.current)) {
                    createExpressAddressId.current = toast.info(t("Select or create an express delivery address"), {
                      autoClose: 2000,
                    });
                  }
                } else {
                  if (!toast.isActive(expressNotAvailableId.current)) {
                    expressNotAvailableId.current = toast.warn(t("Express delivery is not available here"), {
                      autoClose: 2000,
                    });
                  }
                }
              }
            } else {
              if (!toast.isActive(toastTodayId.current)) {
                toastTodayId.current = toast.success(t("We'll deliver today"));
              }
              resetExpressPointId();
            }
          },
        },
        {
          text: t("Cancel"),
          role: "cancel",
        },
      ],
    });
  };

  return (
    <div className={cln("flex items-center justify-center gap-2.5", wrapperClassname)}>
      {modes.map((modeItem) => {
        return (
          <button
            key={modeItem}
            disabled={history.location.pathname.includes("shipping") || history.location.pathname.includes("order")}
            onClick={() => handleModeChange(modeItem as MarketDeliveryType)}
            className={cln(
              mode === modeItem && "!bg-[#EABC2E]",
              "flex flex-col items-center rounded-md py-2 px-[1.063rem] gap-1 min-w-[60px] bg-[#282828]",
            )}
          >
            <IonText className={cln(mode === modeItem && "!text-[#282828]", "text-white text-xs font-bold")}>
              {t(modeItem === "express" ? "In an hour" : "Today")}
            </IonText>
          </button>
        );
      })}
    </div>
  );
};

export default ExpressMarketSwitcher;
